import {
  Button,
  Buttons,
  CancelButton,
  DateTime,
  FormMessage,
  Icon,
  Inline,
  ModalCard,
  Radio,
  RadioGroup,
  TextArea,
  TextInput,
  Tooltip,
} from '~/components';
import { useApi, useSubscription, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';
import { dateFormats } from '~/utils';

const WarningIcon = styled(Icon)`
  font-size: 2.5rem;
  margin-right: 1rem;
`;

const Attestation = styled.div`
  margin-top: 1.5rem;

  textarea {
    resize: none;
  }
`;

const Signature = styled.div`
  margin-top: 1.5rem;

  input {
    font-family: 'Caveat', cursive;
    font-size: 1.4rem;
  }
`;

const dateFormat = 'dddd l';

export default function SubmitTimesheetConfirmation({ date, member, onSubmit, resolve, timesheetContext }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isSubmitting, message, status }, form] = useForm();

  const toast = useToast();

  const { notify } = useSubscription();

  const dates = useMemo(() => {
    return {
      startOfWeek: moment(date).startOf('isoWeek'),
      endOfWeek: moment(date).endOf('isoWeek'),

      get startOfMonth() {
        return moment(this.endOfWeek).startOf('month');
      },

      get endOfMonth() {
        return moment(this.startOfWeek).endOf('month');
      },

      get startOfSemiMonth() {
        return moment(this.startOfWeek).date(16);
      },

      get endOfSemiMonth() {
        return moment(this.startOfWeek).date(15);
      },

      get isSemiMonthCrossOver() {
        return moment(this.startOfWeek).date(15).isBetween(this.startOfWeek, this.endOfWeek, 'day', '[)');
      },

      get isMonthCrossOver() {
        return this.startOfWeek.month() !== this.endOfWeek.month();
      },
    };
  }, [date]);

  const [state, setState] = useState(null);
  const [attestationSignature, setAttestationSignature] = useState('');

  useEffect(() => {
    (async () => {
      const isPreviousMonthTimesheetSubmitted =
        dates.isMonthCrossOver &&
        timesheetContext.isTimesheetSubmitted({
          start: dates.startOfWeek,
          end: dates.endOfMonth,
        });

      const isNextMonthTimesheetSubmitted =
        dates.isMonthCrossOver &&
        timesheetContext.isTimesheetSubmitted({
          start: dates.startOfMonth,
          end: dates.endOfWeek,
        });

      const isPreviousSemiMonthTimesheetSubmitted =
        dates.isSemiMonthCrossOver &&
        timesheetContext.isTimesheetSubmitted({ start: dates.startOfWeek, end: dates.endOfSemiMonth });

      const isNextSemiMonthTimesheetSubmitted =
        dates.isSemiMonthCrossOver &&
        timesheetContext.isTimesheetSubmitted({ start: dates.startOfSemiMonth, end: dates.endOfWeek });

      const periods = [];

      if (dates.isMonthCrossOver && workspace.allowTimesheetSplitAtEndOfMonth) {
        const month = {
          key: 'month',
          start: dates.startOfWeek,
          end: dates.endOfMonth,
          isSubmitted: isPreviousMonthTimesheetSubmitted,
        };

        const { data } = await api.www
          .workspaces(workspace.id)
          .timesheets()
          .submissionSummary({
            start: month.start.format(dateFormats.isoDate),
            end: month.end.format(dateFormats.isoDate),
            memberId: member?.id,
          });

        month.summary = data;

        periods.push(month);
      }

      if (dates.isSemiMonthCrossOver && workspace.allowTimesheetSplitAtEndOfSemiMonth) {
        const semiMonth = {
          key: 'semiMonth',
          start: dates.startOfWeek,
          end: dates.endOfSemiMonth,
          isSubmitted: isPreviousSemiMonthTimesheetSubmitted,
        };

        const { data } = await api.www
          .workspaces(workspace.id)
          .timesheets()
          .submissionSummary({
            start: semiMonth.start.format(dateFormats.isoDate),
            end: semiMonth.end.format(dateFormats.isoDate),
            memberId: member?.id,
          });

        semiMonth.summary = data;

        periods.push(semiMonth);
      }

      let start = dates.startOfWeek;
      if (
        workspace.allowTimesheetSplitAtEndOfMonth &&
        (isPreviousMonthTimesheetSubmitted || isNextMonthTimesheetSubmitted)
      ) {
        start = dates.startOfMonth;
      }
      if (
        workspace.allowTimesheetSplitAtEndOfSemiMonth &&
        (isPreviousSemiMonthTimesheetSubmitted || isNextSemiMonthTimesheetSubmitted)
      ) {
        start = dates.startOfSemiMonth;
      }

      const week = {
        key: 'week',
        start,
        end: dates.endOfWeek,
        isSubmitted: isNextMonthTimesheetSubmitted || isNextSemiMonthTimesheetSubmitted,
      };

      const { data } = await api.www
        .workspaces(workspace.id)
        .timesheets()
        .submissionSummary({
          start: week.start.format(dateFormats.isoDate),
          end: week.end.format(dateFormats.isoDate),
          memberId: member?.id,
        });

      week.summary = data;

      periods.push(week);

      setState({ periods, period: periods.find((p) => !p.isSubmitted) ?? null });
    })();
  }, [dates, api, member?.id, timesheetContext, workspace]);

  if (!state) return null;

  const { period, periods } = state;
  const timesheetAttestation = period?.summary?.timesheetAttestation;
  const isAttestationComplete = !timesheetAttestation || attestationSignature.trim().length >= 3;

  const handleSubmit = async () => {
    if (!isAttestationComplete) {
      form.error({ message: 'You must sign the timesheet attestation before submitting this timesheet.' });
      return;
    }

    form.submit();

    const { start, end } = period;

    try {
      await api.www
        .workspaces(workspace.id)
        .timesheets()
        .submit({
          start: start.format(dateFormats.isoDate),
          end: end.format(dateFormats.isoDate),
          memberId: member?.id,
          attestationSignature,
        });

      form.done();

      await onSubmit();

      toast.success('The timesheet has been submitted.');

      resolve(true);
      notify(useSubscription.keys.refresh_time_approval_count);
    } catch ({ message }) {
      form.error({ message });
    }
  };

  const handlePeriodChange = (value) => setState({ ...state, period: periods.find((p) => p.key === value) });

  const SubmitFooter = () => (
    <ModalCard.Footer>
      <Buttons align="right">
        <CancelButton onClick={() => resolve(false)}>Cancel</CancelButton>
        <Button isLoading={isSubmitting} disabled={!isAttestationComplete} onClick={handleSubmit}>
          Submit Timesheet
        </Button>
      </Buttons>
    </ModalCard.Footer>
  );

  const OKFooter = () => (
    <ModalCard.Footer>
      <Buttons align="right">
        <CancelButton onClick={() => resolve(false)}>OK</CancelButton>
      </Buttons>
    </ModalCard.Footer>
  );

  const Status = () => status && <FormMessage.Error spaceTop>{message}</FormMessage.Error>;

  if (periods.length === 1) {
    if (period?.summary.canSubmit) {
      return (
        <ModalCard title="Submit Timesheet" onClose={() => resolve(false)}>
          <ModalCard.Body>
            <p>
              This will close the time period from <DateTime value={period.start} /> to <DateTime value={period.end} />{' '}
              and route all unapproved time entries for approval.
            </p>

            {timesheetAttestation && (
              <Attestation>
                <TextArea
                  value={timesheetAttestation.attestationText}
                  placeholder="Attestation"
                  materialAlwaysVisible
                  rows={3}
                  disabled
                />

                <Signature>
                  <TextInput
                    value={attestationSignature}
                    placeholder="Signature"
                    maxLength={255}
                    onChange={({ target: { value } }) => setAttestationSignature(value)}
                  />
                </Signature>
              </Attestation>
            )}

            <Status />
          </ModalCard.Body>

          <SubmitFooter />
        </ModalCard>
      );
    } else {
      return (
        <ModalCard title="Submit Timesheet" onClose={() => resolve(false)}>
          <ModalCard.Body>
            <p>You cannot submit this timesheet because you do not have enough time entered for this period.</p>
            <Inline style={{ marginTop: '1rem' }}>
              <WarningIcon icon="exclamation-triangle" color={colors.warning} />
              <p>
                You are submitting {pluralize('hour', period?.summary.hours, true)} and have a capacity of{' '}
                {pluralize('hour', period?.summary.capacity, true)} for this period.
              </p>
            </Inline>

            <Status />
          </ModalCard.Body>

          <OKFooter />
        </ModalCard>
      );
    }
  } else {
    if (periods.some((p) => !p.isSubmitted && p.summary.canSubmit)) {
      return (
        <ModalCard title="Submit Timesheet" onClose={() => resolve(false)}>
          <ModalCard.Body>
            <div>
              <p>This will close the selected time period and route all unapproved time entries for approval.</p>

              <RadioGroup
                style={{ marginTop: '1rem' }}
                direction="vertical"
                name="period"
                value={period?.summary?.canSubmit ? period.key : null}
                onChange={handlePeriodChange}>
                {periods.map((p) => (
                  <Radio
                    key={p.key}
                    value={p.key}
                    label={
                      <Tooltip
                        message={
                          p.isSubmitted
                            ? 'The timesheet has been submitted'
                            : !p.summary.canSubmit
                              ? `You do not have enough time entered for this period.`
                              : null
                        }>
                        {p.end.diff(p.start, 'days') === 6 ? 'Full' : 'Partial'} week through{' '}
                        <DateTime value={p.end} format={dateFormat} />{' '}
                        {!p.summary.canSubmit && (
                          <Icon icon="exclamation-triangle" color={colors.warning} style={{ marginLeft: '0.5rem' }} />
                        )}
                      </Tooltip>
                    }
                    disabled={p.isSubmitted || !p.summary.canSubmit}
                  />
                ))}
              </RadioGroup>

              {timesheetAttestation && (
                <Attestation>
                  <TextArea
                    value={timesheetAttestation.attestationText}
                    placeholder="Attestation"
                    materialAlwaysVisible
                    rows={3}
                    disabled
                  />

                  <Signature>
                    <TextInput
                      value={attestationSignature}
                      placeholder="Signature"
                      maxLength={255}
                      onChange={({ target: { value } }) => setAttestationSignature(value)}
                    />
                  </Signature>
                </Attestation>
              )}
            </div>

            <Status />
          </ModalCard.Body>

          <SubmitFooter />
        </ModalCard>
      );
    } else {
      return (
        <ModalCard title="Submit Timesheet" onClose={() => resolve(false)}>
          <ModalCard.Body>
            <Inline>
              <WarningIcon icon="exclamation-triangle" color={colors.warning} />
              <p>
                You cannot submit this timesheet because you do not have enough time entered for the available periods.
              </p>
            </Inline>

            <Status />
          </ModalCard.Body>

          <OKFooter />
        </ModalCard>
      );
    }
  }
}
